import type {
  AdoptResult,
  CreatePageStudy,
  PageHit,
  PageStudy,
  PageStudySummary,
} from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Prompts for a page: start a study, read it, read a page's hits, and add the
 * questions that cited a page to a brand.
 */

export async function startPageStudy(
  workspaceId: string,
  body: CreatePageStudy,
): Promise<PageStudy | "unavailable"> {
  const response = await api.POST("/api/workspaces/{workspaceId}/page-studies", {
    params: { path: { workspaceId } },
    body: {
      urls: [...body.urls],
      engines: [...body.engines],
      country: body.country,
      language: body.language,
      // Absent asks for a deep search; the key is left out rather than sent empty.
      ...(body.promptsPerPage === undefined
        ? {}
        : { promptsPerPage: body.promptsPerPage }),
    },
  });
  if (response.data === undefined) {
    const status = response.response.status;
    // 501: no model is configured on this deployment, so nothing could write
    // the questions. Said by the screen, not thrown as a fault.
    if (status === 501) return "unavailable";
    // The server's own sentence: which link could not be used and why, or that
    // this month's capture budget cannot pay for the run.
    if (status === 400 || status === 429) {
      throw new Error(response.error.message);
    }
    throw new Error(problem(status, "start that search"));
  }
  return response.data;
}

export async function fetchPageStudies(
  workspaceId: string,
): Promise<readonly PageStudySummary[]> {
  const response = await api.GET("/api/workspaces/{workspaceId}/page-studies", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load your page searches"));
  }
  return response.data.studies;
}

export async function fetchPageStudy(
  workspaceId: string,
  studyId: string,
): Promise<PageStudy> {
  const response = await api.GET("/api/workspaces/{workspaceId}/page-studies/{studyId}", {
    params: { path: { workspaceId, studyId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load that search"));
  }
  return response.data;
}

export async function fetchPageHits(
  workspaceId: string,
  studyId: string,
  pageId: string,
): Promise<readonly PageHit[]> {
  const response = await api.GET(
    "/api/workspaces/{workspaceId}/page-studies/{studyId}/pages/{pageId}/hits",
    { params: { path: { workspaceId, studyId, pageId } } },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load the prompts for that page"));
  }
  return response.data.hits;
}

export async function addPageHitsToBrand(
  workspaceId: string,
  body: { reportId: string; texts: string[] },
): Promise<AdoptResult> {
  const response = await api.POST("/api/workspaces/{workspaceId}/page-studies/adopt", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "add those prompts to the brand"));
  }
  return response.data;
}
